import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Globe, TrendingUp, AlertTriangle, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const JDEuropeArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-yellow-500 text-white">
                <Globe className="h-3 w-3 mr-1" />
                {t('article.jdeurope.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.jdeurope.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.jdeurope.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.jdeurope.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/85451054-9b43-4913-8418-79397aa997d8.jpg"
              alt="European e-commerce adoption regional variations" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-orange-600" />
                  {t('jdeurope.stats.market.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-orange-600">{t('jdeurope.stats.market.value')}</div>
                <p className="text-sm text-gray-600">{t('jdeurope.stats.market.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                  {t('jdeurope.stats.rate.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-red-600">{t('jdeurope.stats.rate.value')}</div>
                <p className="text-sm text-gray-600">{t('jdeurope.stats.rate.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-blue-600" />
                  {t('jdeurope.stats.strategy.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('jdeurope.stats.strategy.value')}</div>
                <p className="text-sm text-gray-600">{t('jdeurope.stats.strategy.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('jdeurope.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('jdeurope.section2.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section3.title')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">High-Season Priorities:</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('jdeurope.section3.highlight')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section4.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('jdeurope.section4.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section5.title')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Opposite Direction Advantage:</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('jdeurope.section5.highlight')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section6.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('jdeurope.section6.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('jdeurope.section6.list')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section7.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Building Carrier Confidence:</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('jdeurope.section7.highlight')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('jdeurope.section8.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('jdeurope.section8.content')}
            </p>

            <div className="bg-yellow-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('jdeurope.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-yellow-600 border-yellow-200"
                onClick={() => window.open('https://mykn.kuehne-nagel.com/news/article/emerging-economies-to-play-a-bigger-role-in', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default JDEuropeArticle;